import api from './api';

export interface AdminUserListItem {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  createdAt: string;
  subscription?: {
    id: string;
    tierKey: string;
    status: string;
    currentPeriodEnd?: string;
    accessExpiresAt?: string;
  } | null;
}

export interface AdminSubscriptionItem {
  _id: string;
  tierKey: string;
  status: string;
  userId?: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    role?: string;
    status?: string;
  };
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  accessExpiresAt?: string;
  updatedAt: string;
}

export interface AdminUsageItem {
  _id: string;
  userId?: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  tierKey: string;
  reportsGenerated: number;
  reportsDownloaded: number;
  savedReportsUsed: number;
  periodStart?: string;
  periodEnd?: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminWebhookEvent {
  _id: string;
  eventId: string;
  eventType: string;
  status: string;
  createdAt: string;
  processedAt?: string;
}

export interface AdminEmailLog {
  _id: string;
  email: string;
  template?: string;
  templateKey?: string;
  subject?: string;
  status: string;
  createdAt: string;
  errorMessage?: string;
}

export const TIER_OPTIONS = [
  'scout',
  'explorer',
  'navigator',
  'strategist',
  'portfolio_builder',
  'capital_architect',
] as const;

export const TIER_LABELS: Record<(typeof TIER_OPTIONS)[number], string> = {
  scout: 'Scout',
  explorer: 'Explorer',
  navigator: 'Navigator',
  strategist: 'Strategist',
  portfolio_builder: 'Portfolio Builder',
  capital_architect: 'Capital Architect',
};

/** Default report allowance from package plan (null = unlimited). */
export const TIER_REPORT_ALLOWANCES: Record<(typeof TIER_OPTIONS)[number], number | null> = {
  scout: 1,
  explorer: 200,
  navigator: null,
  strategist: null,
  portfolio_builder: null,
  capital_architect: null,
};

export const ACCOUNT_TYPE_OPTIONS = ['PAID', 'TRIAL', 'COMPLIMENTARY'] as const;

export const ACCOUNT_TYPE_LABELS: Record<(typeof ACCOUNT_TYPE_OPTIONS)[number], string> = {
  PAID: 'Paid',
  TRIAL: 'Trial',
  COMPLIMENTARY: 'Complimentary',
};

export const USER_STATUS_OPTIONS = ['ACTIVE', 'INACTIVE', 'SUSPENDED'] as const;
export const USER_ROLE_OPTIONS = ['USER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN'] as const;
export const STAFF_ROLE_OPTIONS = ['MANAGER', 'ADMIN', 'SUPER_ADMIN'] as const;

export const STAFF_ROLE_LABELS: Record<(typeof STAFF_ROLE_OPTIONS)[number], string> = {
  MANAGER: 'Staff (Manager)',
  ADMIN: 'Admin',
  SUPER_ADMIN: 'Super Admin',
};

export interface CreateStaffUserPayload {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  role: (typeof STAFF_ROLE_OPTIONS)[number];
}

export interface CreateCustomerUserPayload {
  email: string;
  firstName: string;
  lastName: string;
  tierKey: (typeof TIER_OPTIONS)[number];
  accountType: (typeof ACCOUNT_TYPE_OPTIONS)[number];
  accessStartDate?: string;
  accessEndDate?: string | null;
  reportsAllowance?: number | null;
  sendWelcomeInvitation?: boolean;
  internalNotes?: string;
}

export interface CreateCustomerUserResponse {
  user: AdminUserListItem;
  subscription: AdminSubscriptionItem;
  invitationSent: boolean;
  emailStatus?: string | null;
  emailError?: string | null;
}

export const SUBSCRIPTION_STATUS_OPTIONS = [
  'ACTIVE',
  'PAST_DUE',
  'PAYMENT_FAILED',
  'CANCELLED',
  'EXPIRED',
  'INACTIVE',
] as const;

export const adminService = {
  async listUsers(page = 1, limit = 20, search = ''): Promise<PaginatedResponse<AdminUserListItem>> {
    const response = await api.get<PaginatedResponse<AdminUserListItem>>('/admin/users', {
      params: { page, limit, search: search || undefined },
    });
    return response.data;
  },

  async listSubscriptions(page = 1, limit = 20): Promise<PaginatedResponse<AdminSubscriptionItem>> {
    const response = await api.get<PaginatedResponse<AdminSubscriptionItem>>('/admin/subscriptions', {
      params: { page, limit },
    });
    return response.data;
  },

  async listUsage(page = 1, limit = 20): Promise<PaginatedResponse<AdminUsageItem>> {
    const response = await api.get<PaginatedResponse<AdminUsageItem>>('/admin/usage', {
      params: { page, limit },
    });
    return response.data;
  },

  async listWebhookEvents(page = 1, limit = 20): Promise<PaginatedResponse<AdminWebhookEvent>> {
    const response = await api.get<PaginatedResponse<AdminWebhookEvent>>('/admin/webhook-events', {
      params: { page, limit },
    });
    return response.data;
  },

  async listEmailLogs(page = 1, limit = 20): Promise<PaginatedResponse<AdminEmailLog>> {
    const response = await api.get<PaginatedResponse<AdminEmailLog>>('/admin/email-logs', {
      params: { page, limit },
    });
    return response.data;
  },

  async createStaffUser(payload: CreateStaffUserPayload): Promise<AdminUserListItem> {
    const response = await api.post<{ user: AdminUserListItem }>('/admin/users', payload);
    return response.data.user;
  },

  async createCustomerUser(payload: CreateCustomerUserPayload): Promise<CreateCustomerUserResponse> {
    const response = await api.post<CreateCustomerUserResponse>('/admin/users/customer', payload);
    return response.data;
  },

  async updateUserStatus(userId: string, status: string): Promise<void> {
    await api.patch(`/admin/users/${userId}/status`, { status });
  },

  async updateUserRole(userId: string, role: string): Promise<void> {
    await api.patch(`/admin/users/${userId}/role`, { role });
  },

  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/admin/users/${userId}`);
  },

  async updateSubscription(
    subscriptionId: string,
    payload: { tierKey?: string; status?: string }
  ): Promise<void> {
    await api.patch(`/admin/subscriptions/${subscriptionId}`, payload);
  },
};
